import { shopifyGraphqlIdToAdminId } from '@utils/shopifyIds';
import { isAfter } from 'date-fns';
import { GetStaticPaths, GetStaticProps } from 'next';
import Head from 'next/head';
import { StoryData } from 'storyblok-js-client';

import { ssgClient } from '../../../apollo/api/avantArteClient';
import { Provider as ManifoldProvider } from '../../../components/Auction/Manifold/Provider';
import Layout from '../../../components/Layout';
import Works from '../../../components/PageTypes/Works';
import { FIFTEEN_MINUTES_IN_SECONDS, WORKS_PER_PAGE } from '../../../constants';
import { FIRST_SUPPORTED_DRAW_LAUNCH } from '../../../constants/product';
import { pathFor, toCanonicalUrl } from '../../../helpers/links';
import { imageOgStaticLoader } from '../../../helpers/og-image';
import {
  GeneratedGetDrawByProductIDsQuery,
  queryGetDrawByProductIDs,
} from '../../../types/generated';
import { castArray } from '../../../utils/arrays';
import {
  isAuctionRelease,
  isDrawRelease,
  mapShopifyProductsToProducts,
} from '../../../utils/product';
import { fetchAllProducts } from '../../../utils/shopify/fetchAllProducts';
import {
  getStoryblokPage,
  StoryResponse,
  useStoryblok,
} from '../../../utils/storyblok';
import { pageTranslations } from '../../../utils/translations';
import { getUrl } from '../../../utils/urlHelpers';

interface Props {
  allWorks: Product[];
  draws: GeneratedGetDrawByProductIDsQuery['getDrawByProductIDs'];
  locale: string;
  locales: string[];
  page: StoryData<ProductsIndexPageType>;
  pagination: {
    page: number;
    perPage: number;
    total: number;
  };
  preview: boolean;
  ogImageUrl: string;
}

const Page = ({
  allWorks,
  draws,
  locale,
  locales,
  ogImageUrl,
  page,
  pagination,
  preview,
}: Props) => {
  const story = useStoryblok(page, preview, locale);

  return (
    <>
      <Head>
        {pagination.page !== 1 ? (
          <link
            href={getUrl(
              process.env.NEXT_PUBLIC_HOSTNAME,
              pathFor({ page: 'WORKS_INDEX', pageNumber: pagination.page - 1 }),
            )}
            rel="prev"
          />
        ) : null}
        {pagination.page < pagination.total ? (
          <link
            href={getUrl(
              process.env.NEXT_PUBLIC_HOSTNAME,
              pathFor({ page: 'WORKS_INDEX', pageNumber: pagination.page + 1 }),
            )}
            rel="next"
          />
        ) : null}
      </Head>
      <Layout
        canonicalUrl={toCanonicalUrl({
          path: pathFor({ page: 'WORKS_INDEX', pageNumber: pagination.page }),
          locale,
          hostname: String(process.env.NEXT_PUBLIC_HOSTNAME),
        })}
        description={story.content.seo?.description}
        locale={locale}
        locales={locales}
        ogImageUrl={ogImageUrl}
        title={story.content.seo?.title}
      >
        <ManifoldProvider>
          <Works
            allWorks={allWorks}
            blok={story.content}
            draws={draws}
            pagination={pagination}
          />
        </ManifoldProvider>
      </Layout>
    </>
  );
};

export const getStaticProps: GetStaticProps = async (ctx) => {
  const { locale = 'en', locales = ['en'], params, preview = false } = ctx;
  const [number] = castArray(params?.number || '1');

  const [page, translations, worksFromApi, ogImageUrl] = await Promise.all([
    getStoryblokPage('cdn/stories/products', { locale, preview }),
    pageTranslations(locale, ['filters', 'works', 'og']),
    fetchAllProducts('tag:Listing page'),
    imageOgStaticLoader({
      titleKey: { key: 'og:works.index.title' },
      imageSrc:
        'https://a.storyblok.com/f/119968/4000x2667/846735f337/parra-atelier-2.jpg',
      imageWidth: 1200,
      imageHeight: 630,
      overlay: true,
      locale,
    }),
  ]);

  if (!page) return { notFound: true };

  const allWorks = mapShopifyProductsToProducts(
    worksFromApi.map((work) => work.node),
  );

  const drawsIds = allWorks
    .filter(
      ({ launchDate, releaseType }) =>
        (isDrawRelease(releaseType) || isAuctionRelease(releaseType)) &&
        launchDate &&
        isAfter(new Date(launchDate), FIRST_SUPPORTED_DRAW_LAUNCH),
    )
    .map((i) => shopifyGraphqlIdToAdminId(i.id));

  const draws = drawsIds.length
    ? await queryGetDrawByProductIDs(ssgClient(ctx), {
        productIDs: drawsIds,
      }).then(({ data }) => data.getDrawByProductIDs)
    : [];

  return {
    props: {
      allWorks,
      draws,
      ogImageUrl,
      locale,
      locales,
      page: (page as StoryResponse<ProductsIndexPageType>)?.data.story || false,
      pagination: {
        page: parseInt(number, 10),
        perPage: WORKS_PER_PAGE,
        total: Math.floor(allWorks.length / WORKS_PER_PAGE) + 1,
      },
      preview,
      ...translations,
    },
    revalidate: FIFTEEN_MINUTES_IN_SECONDS,
  };
};

export const getStaticPaths: GetStaticPaths = () => ({
  fallback: 'blocking',
  paths: [{ params: { number: '1', locale: 'en' } }],
});

export default Page;
